import { useState, useEffect } from "react";
import { api, errMsg, fmtMoney, fmtDate } from "../../lib/api";
import { StatusBadge } from "../../components/shared/StatusBadge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, CheckCircle2 } from "lucide-react";

export default function Invoices() {
  const [invoices, setInvoices] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ customer_name: "", customer_email: "", due_date: "" });
  const [items, setItems] = useState([{ description: "", qty: 1, unit_price: 0 }]);

  const load = () => api.get("/invoices").then((r) => setInvoices(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const subtotal = items.reduce((a, i) => a + (Number(i.qty) || 0) * (Number(i.unit_price) || 0), 0);

  const create = async () => {
    try {
      await api.post("/invoices", {
        ...form,
        line_items: items.map((i) => ({ description: i.description, qty: Number(i.qty) || 1, unit_price: Number(i.unit_price) || 0 })),
      });
      toast.success("Invoice issued");
      setCreateOpen(false);
      setForm({ customer_name: "", customer_email: "", due_date: "" });
      setItems([{ description: "", qty: 1, unit_price: 0 }]);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const markPaid = async (id) => {
    await api.patch(`/invoices/${id}`, { status: "paid" });
    toast.success("Marked as paid");
    load();
  };

  return (
    <div data-testid="ops-invoices-page">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <p className="overline-cc">Billing</p>
          <h1 className="page-title mt-1.5">Invoices & Payments</h1>
        </div>
        <Button data-testid="invoices-new-button" onClick={() => setCreateOpen(true)} className="rounded-full btn-glow bg-[var(--cc-accent)] text-[var(--cc-accent-fg)] hover:bg-[var(--cc-accent)]/90 font-semibold">
          <Plus className="w-4 h-4 mr-1" /> New Invoice
        </Button>
      </div>

      <div className="rounded-2xl border bg-card overflow-x-auto">
        <table className="w-full text-sm min-w-[700px]">
          <thead>
            <tr className="border-b bg-secondary/50">
              {["Invoice", "Customer", "Total", "Status", "Due", "Issued", ""].map((h, i) => (
                <th key={i} className="text-left px-5 py-3 th-cc font-normal">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y">
            {invoices.map((inv) => (
              <tr key={inv.id} data-testid={`invoice-row-${inv.invoice_number}`} className="hover:bg-secondary/40 transition-colors duration-150">
                <td className="px-6 py-4 font-mono-cc text-[var(--cc-accent)]">{inv.invoice_number}</td>
                <td className="px-6 py-4">
                  <p className="font-semibold">{inv.customer_name}</p>
                  <p className="text-xs text-muted-foreground">{inv.customer_email}</p>
                </td>
                <td className="px-6 py-4 font-semibold">{fmtMoney(inv.total)}</td>
                <td className="px-6 py-4"><StatusBadge status={inv.status} /></td>
                <td className="px-6 py-4 text-muted-foreground text-xs">{fmtDate(inv.due_date)}</td>
                <td className="px-6 py-4 text-muted-foreground text-xs">{fmtDate(inv.created_at)}</td>
                <td className="px-6 py-4">
                  {["open", "overdue"].includes(inv.status) && (
                    <Button data-testid={`invoice-mark-paid-${inv.invoice_number}`} size="sm" variant="outline" className="rounded-full text-xs" onClick={() => markPaid(inv.id)}>
                      <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Mark Paid
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-xl" data-testid="invoice-create-dialog">
          <DialogHeader><DialogTitle className="font-heading">New Invoice</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>Customer name</Label><Input data-testid="invoice-customer-name" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Customer email</Label><Input data-testid="invoice-customer-email" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} /></div>
            </div>
            <div className="space-y-1.5"><Label>Due date</Label><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></div>
            <div>
              <Label>Line items</Label>
              <div className="space-y-2 mt-2">
                {items.map((it, i) => (
                  <div key={i} className="flex gap-2">
                    <Input data-testid={`invoice-item-desc-${i}`} className="flex-1" placeholder="Description" value={it.description} onChange={(e) => setItems(items.map((x, j) => (j === i ? { ...x, description: e.target.value } : x)))} />
                    <Input className="w-16" type="number" placeholder="Qty" value={it.qty} onChange={(e) => setItems(items.map((x, j) => (j === i ? { ...x, qty: e.target.value } : x)))} />
                    <Input data-testid={`invoice-item-price-${i}`} className="w-28" type="number" placeholder="Price" value={it.unit_price} onChange={(e) => setItems(items.map((x, j) => (j === i ? { ...x, unit_price: e.target.value } : x)))} />
                    {items.length > 1 && (
                      <button onClick={() => setItems(items.filter((_, j) => j !== i))} className="p-2 text-red-500" aria-label="Remove"><Trash2 className="w-4 h-4" /></button>
                    )}
                  </div>
                ))}
                <Button variant="ghost" size="sm" className="rounded-full" onClick={() => setItems([...items, { description: "", qty: 1, unit_price: 0 }])}>
                  <Plus className="w-3.5 h-3.5 mr-1" /> Add line
                </Button>
              </div>
            </div>
            <div className="rounded-lg border p-4 text-sm space-y-1">
              <div className="flex justify-between text-muted-foreground"><span>Subtotal</span><span>{fmtMoney(subtotal)}</span></div>
              <div className="flex justify-between text-muted-foreground"><span>Tax (8.25%)</span><span>{fmtMoney(subtotal * 0.0825)}</span></div>
              <div className="flex justify-between font-heading font-bold text-base pt-1"><span>Total</span><span>{fmtMoney(subtotal * 1.0825)}</span></div>
            </div>
            <Button data-testid="invoice-create-submit" onClick={create} disabled={!form.customer_name || subtotal <= 0} className="w-full rounded-full btn-glow bg-[var(--cc-accent)] text-[var(--cc-accent-fg)] hover:bg-[var(--cc-accent)]/90 font-semibold">Issue Invoice</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
